import React from "react";
import { Link } from "react-router";
import { useGetBusinessReportsOutcomeQuery } from "@/modules/snapshots/businessReports.api";
import { useRecentItems } from "@/hooks/use-add-recent-items";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ClipboardIcon, Clock, FolderClosedIcon, MessageSquare } from "lucide-react";

interface Props {
  children: React.ReactNode;
}

const truncateText = (text: string | null, maxLength: number = 30): string => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

export const RecentDropdown: React.FC<Props> = ({ children }) => {
  const { data: snapshots } = useGetBusinessReportsOutcomeQuery(
    { descending: true, order_by: "created_at" },
    { refetchOnMountOrArgChange: true },
  );
  const { recentFiles, recentFolders } = useRecentItems();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        {/* Folders Submenu */}
        {recentFolders.length > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <FolderClosedIcon className="mr-2 h-4 w-4" />
              Folders
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-96">
              {recentFolders
                .filter(row => row.name)
                .slice(0, 5)
                .map(folder => (
                  <DropdownMenuItem key={folder.id} asChild>
                    <Link to={folder.url} className="block cursor-pointer" title={folder.name}>
                      {truncateText(folder.name)}
                    </Link>
                  </DropdownMenuItem>
                ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {/* Files Submenu */}
        {recentFiles.length > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <MessageSquare className="mr-2 h-4 w-4" />
              Files
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-96">
              {recentFiles
                .filter(row => row.name)
                .slice(0, 5)
                .map(file => (
                  <DropdownMenuItem key={file.id} asChild>
                    <Link to={file.url} className="block cursor-pointer" title={file.name}>
                      {truncateText(file.name)}
                    </Link>
                  </DropdownMenuItem>
                ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {/* Snapshots Submenu */}
        {snapshots?.results && snapshots.results.length > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <ClipboardIcon className="mr-2 h-4 w-4" />
              Snapshots
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-96">
              {snapshots.results
                .filter(row => row.name)
                .slice(0, 5)
                .map(row => (
                  <DropdownMenuItem key={row.id} asChild>
                    <Link to={`/reports/outcome/${row.id}`} className="block cursor-pointer" title={row.name}>
                      {truncateText(row.name)}
                    </Link>
                  </DropdownMenuItem>
                ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {/* Empty state */}
        {recentFiles.length === 0 &&
          recentFolders.length === 0 &&
          (!snapshots?.results || snapshots.results.length === 0) && (
            <DropdownMenuItem disabled>
              <Clock className="mr-2 h-4 w-4" />
              No recent items
            </DropdownMenuItem>
          )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
